// src/utilidades/seoTienda.js
// SEO de la tienda pública (SPA): título, descripción, canonical, Open Graph y
// datos estructurados schema.org (Store + Product con precio en PEN y stock).
// Google ejecuta JavaScript y los lee; las vistas previas de WhatsApp/Facebook
// no (esas usan las metas de index.html). Sin lecturas ni Functions nuevas: sale
// del mismo catálogo que ya se descargó del CDN.
import { fotoDeProducto } from '../dominio/logicaTiendaPublica';
import { normalizarMoneda } from '../dominio/moneda';

const MAX_PRODUCTOS_LD = 50;
const recortar = (texto, max) => {
  const limpio = String(texto || '').replace(/\s+/g, ' ').trim();
  return limpio.length > max ? `${limpio.slice(0, max - 1).trimEnd()}…` : limpio;
};
const httpsValida = (url) => (typeof url === 'string' && /^https:\/\/[^\s"'<>]+$/.test(url) ? url : '');

/** Datos de SEO de una tienda, sin tocar el documento (función pura). */
export const datosSeoTienda = ({ catalogo, gymId, origen }) => {
  const nombre = recortar(catalogo?.nombre, 100) || 'Tienda';
  const productos = Array.isArray(catalogo?.productos) ? catalogo.productos : [];
  const url = `${origen}/catalogo/${encodeURIComponent(gymId)}`;
  const categorias = [...new Set(productos.map((p) => recortar(p.categoria, 40)).filter((c) => c && c !== 'General'))].slice(0, 3);
  const envio = catalogo?.deliveryHabilitado ? ', delivery' : '';
  const provincia = catalogo?.envioProvinciaHabilitado ? ' y envíos a provincia' : '';
  const descripcion = recortar(catalogo?.descripcion
    || `Compra en la tienda online de ${nombre}${categorias.length ? `: ${categorias.join(', ')}` : ''}. Pedidos por WhatsApp${envio}${provincia}.`, 160);
  const imagen = productos.map((p) => httpsValida(fotoDeProducto(p))).find(Boolean) || '';

  const tienda = {
    '@type': 'Store', '@id': `${url}#tienda`, name: nombre, url, description: descripcion,
    ...(imagen ? { image: imagen } : {}),
    ...(/^519\d{8}$/.test(catalogo?.whatsapp || '') ? { telephone: `+${catalogo.whatsapp}` } : {}),
    ...(recortar(catalogo?.direccion, 200) ? { address: { '@type': 'PostalAddress', streetAddress: recortar(catalogo.direccion, 200), addressCountry: 'PE' } } : {}),
    currenciesAccepted: normalizarMoneda(catalogo?.moneda),
  };
  const ofertas = productos.slice(0, MAX_PRODUCTOS_LD).filter((p) => Number(p.precio) > 0).map((p) => {
    const foto = httpsValida(fotoDeProducto(p));
    const extra = (Array.isArray(p.fotosExtra) ? p.fotosExtra : []).map(httpsValida).filter(Boolean);
    return {
      '@type': 'Product', name: recortar(p.nombre, 120),
      ...(foto ? { image: [...new Set([foto, ...extra])] } : {}),
      ...(recortar(p.descripcion, 300) ? { description: recortar(p.descripcion, 300) } : {}),
      ...(p.sku ? { sku: recortar(p.sku, 80) } : {}),
      ...(recortar(p.marca, 60) ? { brand: { '@type': 'Brand', name: recortar(p.marca, 60) } } : {}),
      ...(recortar(p.categoria, 80) ? { category: recortar(p.categoria, 80) } : {}),
      offers: {
        '@type': 'Offer', price: Number(p.precio).toFixed(2), priceCurrency: normalizarMoneda(catalogo?.moneda),
        availability: Number(p.stock) > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        url: `${url}#p-${encodeURIComponent(p.id)}`, seller: { '@id': `${url}#tienda` },
      },
    };
  });
  return {
    titulo: `${nombre} | Tienda online`,
    descripcion, url, imagen,
    jsonLd: { '@context': 'https://schema.org', '@graph': [tienda, ...ofertas] },
  };
};

// Crea (o reutiliza) una etiqueta del <head> y recuerda cómo dejarla al salir.
const etiqueta = (selector, crear, deshacer) => {
  let nodo = document.head.querySelector(selector);
  if (nodo) {
    const previo = nodo.getAttribute('content') ?? nodo.getAttribute('href');
    deshacer.push(() => { if (nodo.hasAttribute('content')) nodo.setAttribute('content', previo ?? ''); else nodo.setAttribute('href', previo ?? ''); });
    return nodo;
  }
  nodo = crear();
  document.head.appendChild(nodo);
  deshacer.push(() => nodo.remove());
  return nodo;
};
const meta = (atributo, clave, valor, deshacer) => {
  etiqueta(`meta[${atributo}="${clave}"]`, () => {
    const m = document.createElement('meta'); m.setAttribute(atributo, clave); return m;
  }, deshacer).setAttribute('content', valor);
};

/** Aplica el SEO de la tienda al documento. Devuelve la función que lo deshace. */
export const aplicarSeoTienda = (datos) => {
  const deshacer = [];
  const tituloAnterior = document.title;
  document.title = datos.titulo;
  deshacer.push(() => { document.title = tituloAnterior; });
  meta('name', 'description', datos.descripcion, deshacer);
  meta('property', 'og:type', 'website', deshacer);
  meta('property', 'og:locale', 'es_PE', deshacer);
  meta('property', 'og:title', datos.titulo, deshacer);
  meta('property', 'og:description', datos.descripcion, deshacer);
  meta('property', 'og:url', datos.url, deshacer);
  if (datos.imagen) meta('property', 'og:image', datos.imagen, deshacer);
  etiqueta('link[rel="canonical"]', () => {
    const l = document.createElement('link'); l.setAttribute('rel', 'canonical'); return l;
  }, deshacer).setAttribute('href', datos.url);
  const ld = document.createElement('script');
  ld.type = 'application/ld+json';
  ld.id = 'ld-tienda';
  // «<» escapado: un nombre de producto nunca cierra el <script>.
  ld.textContent = JSON.stringify(datos.jsonLd).replace(/</g, '\\u003c');
  document.getElementById('ld-tienda')?.remove();
  document.head.appendChild(ld);
  deshacer.push(() => ld.remove());
  return () => deshacer.reverse().forEach((f) => f());
};
